/*
 * BAA compliance-dashboard service (frontend, admin-only).
 *
 * Compat SDK only. Reads are gated by Firestore rules to the admin custom
 * claim; mutations that must run server-side (initiate signing, signed
 * download URL) go through callables in functions/src/baa.
 */
import firebaseApp, { db } from "services/firebase";
import { FUNCTIONS_REGION } from "shared/services";

function baaCallable(name) {
  return firebaseApp.functions(FUNCTIONS_REGION).httpsCallable(name);
}

const mapDoc = (doc) => ({ id: doc.id, ...doc.data() });

/**
 * Subscribe to all BAA requests, newest first.
 * @param {(requests: object[]) => void} onChange
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeToBaaRequests(onChange, onError) {
  return db
    .collection("baaRequests")
    .orderBy("submittedAt", "desc")
    .onSnapshot(
      (snap) => onChange(snap.docs.map(mapDoc)),
      (err) => onError && onError(err)
    );
}

/**
 * Subscribe to a single BAA request.
 * @param {string} requestId
 * @param {(request: object|null) => void} onChange
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeToBaaRequest(requestId, onChange, onError) {
  return db
    .collection("baaRequests")
    .doc(requestId)
    .onSnapshot(
      (doc) => onChange(doc.exists ? mapDoc(doc) : null),
      (err) => onError && onError(err)
    );
}

/**
 * Subscribe to a request's audit log in chronological order.
 * @param {string} requestId
 * @param {(entries: object[]) => void} onChange
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeToAuditLog(requestId, onChange, onError) {
  return db
    .collection("baaRequests")
    .doc(requestId)
    .collection("auditLog")
    .orderBy("timestamp", "asc")
    .onSnapshot(
      (snap) => onChange(snap.docs.map(mapDoc)),
      (err) => onError && onError(err)
    );
}

/**
 * Save internal notes on a request (admin-writable per Firestore rules).
 * @param {string} requestId
 * @param {string} notes
 */
export async function updateBaaRequestNotes(requestId, notes) {
  await db.collection("baaRequests").doc(requestId).update({ notes });
}

/**
 * Initiate signing for a pending request (creates the DocuSign envelope).
 * @param {string} requestId
 * @returns {Promise<{ success: boolean }>}
 */
export async function initiateBAASigning(requestId) {
  const fn = baaCallable("initiateBAASigningCallable");
  const result = await fn({ requestId });
  return result.data;
}

/**
 * Mint a fresh in-browser signing link for a request awaiting the institution's
 * signature (recovery for an abandoned/expired embedded-signing session).
 * @param {string} requestId
 * @returns {Promise<{ url: string }>}
 */
export async function regenerateBAASigningUrl(requestId) {
  const fn = baaCallable("regenerateBAASigningUrlCallable");
  const result = await fn({ requestId });
  return result.data;
}

/**
 * Get a short-lived signed URL for the executed BAA PDF.
 * @param {string} requestId
 * @returns {Promise<{ url: string }>}
 */
export async function getBAAdocumentUrl(requestId) {
  const fn = baaCallable("getBAAdocumentUrl");
  const result = await fn({ requestId });
  return result.data;
}
